import dayjs from 'dayjs';
import { useListContext } from 'ra-core';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleFilled } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import { ListProps, TextField, FunctionField } from 'react-admin';

import {
  TextInput,
  Grid,
  GridList,
  Forbidden,
  ButtonLinkField,
  SelectInput,
  SelectArrayInput,
  DateRangeSelect,
  DateField,
  CountUsersBtn,
  CountCreditsBtn,
} from '@components';
import { BuyerCreator } from '../BuyerCreator';
import { useSettingsContext } from '@providers';
import { DealerListActions } from '../DealerListActions';
import { adminSettingsHttp, dealerHttp } from '@network';
import { useExporter, useRbacEdit, useRbacList } from '@hooks';
import { AdminOptionCats, CreditStatus, Dealer } from '@types';
import { CreditCropsFilter } from '@pages/credit/CreditCropsFilter';
import { financeSettingsHttp } from '@network/finance-settings-http';
import { CreditStatesFilter } from '@pages/credit/CreditStatesFilter';
import { DealerChannelFilter, DealerOwnerFilter } from '@pages/dealer/components';
import cls from './DealerList.module.css';

const Summary = () => {
  const { data, selectedIds } = useListContext<Dealer>();
  if (!data || !selectedIds.length) return null;
  const list: Dealer[] =
    selectedIds.length > 0
      ? (data || []).filter((i) => selectedIds.includes(i.id))
      : (data || []);

  const usersTotal = list
    .reduce((acc, curr) => acc + (curr.countUsers || 0), 0);
  const usersAvg = +(usersTotal / list.length).toFixed(0);
  const activeCreditsTotal = list
    .reduce((acc, curr) => acc + (curr.countActiveCredits || 0), 0);
  const activeCreditsAvg = +(activeCreditsTotal / list.length).toFixed(0);
  const applyingCreditsTotal = list
    .reduce((acc, curr) => acc + (curr.countApplyingCredits || 0), 0);
  const applyingCreditsAvg = +(applyingCreditsTotal / list.length).toFixed(0);
  const signedCreditsTotal = list
    .reduce((acc, curr) => acc + (curr.countSignedCredits || 0), 0);
  const signedCreditsAvg = +(signedCreditsTotal / list.length).toFixed(0);

  return (
    <>
      <span className={cls.totalBlock}>
        <span>Total Users: {usersTotal}</span>
        <span>AVG Users: {usersAvg}</span>
      </span>
      <span style={{ paddingRight: 14 }} />
      <span className={cls.totalBlock}>
        <span>Total Active Credits: {activeCreditsTotal}</span>
        <span>AVG Active Credits: {activeCreditsAvg}</span>
      </span>
      <span style={{ paddingRight: 14 }} />
      <span className={cls.totalBlock}>
        <span>Total Applying Credits: {applyingCreditsTotal}</span>
        <span>AVG Applying Credits: {applyingCreditsAvg}</span>
      </span>
      <span style={{ paddingRight: 14 }} />
      <span className={cls.totalBlock}>
        <span>Total Signed credits: {signedCreditsTotal}</span>
        <span>AVG Signed credits: {signedCreditsAvg}</span>
      </span>
    </>
  );
};

export const DealerAssociateList = (props: ListProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dealerId = searchParams.get('dealerId');

  const rbacList = useRbacList();
  const rbacEdit = useRbacEdit();
  const {
    isBuyersCreatorActive, setIsBuyersCreatorActive, setIsToolbarShown,
  } = useSettingsContext();
  const modalType = useRef<'create' | 'edit'>('create');
  const [selectedBuyer, setSelectedBuyer] = useState<Dealer | null>(null);
  const [activationOptions, setActivationOptions] = useState<{id: number; name: string}[]>([]);
  const [dealerStatuses, setDealerStatuses] = useState<{id: string, name: string}[]>([]);
  const [dealerTypes, setDealerTypes] = useState<{id: string, name: string}[]>([]);
  const months = [...dayjs.months(), 'All year'];

  const title = 'Associates';

  const exporter = useExporter({
    filename: `Verqor ${title}`,
    props: {
      'code': {
        label: 'Code',
      },
      'id': {
        label: 'ID',
      },
      'activationTitle': {
        label: 'Activation',
      },
      'name': {
        label: 'Name',
      },
      'cropsIbmMonthList': {
        label: 'IBM',
      },
      'cropIbms': {
        prop: 'cropsIbm',
        label: 'Unique Crops',
        transform: (v?: { crop: string, ibm: string }[]) => {
          if (!v || !Array.isArray(v)) return '';
          const cropNames = v.map(cropIbm => cropIbm.crop).filter(Boolean);

          return cropNames.length > 0 ? cropNames.join(', ') : '';
        },
      },
      'phone': {
        label: 'Phone',
        transform: 'phone',
      },
      'status.name': {
        label: 'Status',
      },
      'priorityObj.name': {
        label: 'Type',
      },
      'addedByUserFullName': {
        label: 'Added by User',
      },
      'owner.name': {
        label: 'Owner',
        prop: 'owner.name',
      },
      'countUsers': {
        label: 'Farmers',
      },
      'sfAccountLink': {
        label: 'SalesForce Account',
      },
      'source': {
        label: 'Channel',
      },
      'owner': {
        label: 'Owner',
      },
      'createdAt': {
        label: 'Creation Date',
        transform: 'datetime',
      },
      'closeDate': {
        label: 'Close Date',
        transform: 'datetime',
      },
      'userCreatedAt': {
        label: 'User Registration Date',
        transform: 'datetime',
      },
      'documents': {
        label: 'Documents',
        transform: 'documentsFilled',
      },
      'countApplyingCredits': {
        label: 'Count Applying Credits',
      },
      'countSignedCredits': {
        label: 'Count Signed Credits',
      },
      'countActiveCredits': {
        label: 'Count Active Credits',
      },
    },
  });

  const openCreator = () => {
    modalType.current = 'create';
    setIsBuyersCreatorActive(true);
    setIsToolbarShown(false);
  };

  const closeCreator = () => {
    modalType.current = 'create';
    setIsBuyersCreatorActive(false);
    setIsToolbarShown(true);
    setSelectedBuyer(null);
  };

  useEffect(() => {
    (async () => {
      try {
        const nextActivations = await financeSettingsHttp.getActivations();
        const dealerStatusesObjects = await adminSettingsHttp
          .getOptionsByCategory(AdminOptionCats.PARTNERS_STATUS);
        const dealerTypesObjects = await adminSettingsHttp
          .getOptionsByCategory(AdminOptionCats.PARTNERS_TYPE_ASSOCIATE);

        if (nextActivations) {
          setActivationOptions(nextActivations.map(activation => ({
            id: activation.id,
            name: activation.activation,
          })));
        }

        if (dealerStatusesObjects?.length) {
          setDealerStatuses(dealerStatusesObjects.map(i => ({
            id: i.name,
            name: i.name,
          })));
        }

        if (dealerTypesObjects?.length) {
          setDealerTypes(dealerTypesObjects.map(i => ({
            id: i.name,
            name: i.name,
          })));
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    if (dealerId) {
      (async () => {
        modalType.current = 'edit';
        const currentDealer = await dealerHttp.getById(+dealerId);

        setSelectedBuyer(currentDealer);
        setIsBuyersCreatorActive(true);

        searchParams.delete('dealerId');
        setSearchParams(searchParams);
      })();
    }
  }, [dealerId]);

  const filters = [
    <TextInput source="q" name="q" key="q" variant="outlined" label="Search"
      alwaysOn style={{ width: 200 }} inputProps={{ maxLength: 40 }}
    />,
    <SelectInput
      key="activationId"
      source="activationId"
      label="Activation"
      choices={activationOptions}
      className={'MS-field'}
    />,
    <TextInput source="addedBy" name="addedBy" key="addedBy"
      variant="outlined" label="Added by user" alwaysOn
      style={{ width: 200 }} inputProps={{ maxLength: 40 }}
    />,
    <DateRangeSelect
      key="dates"
      source="dates"
      label="Creation Date / Close Date / Users / Credits"
      labelClass={cls.labelClass}
      alwaysOn
      choices={[
        { id: 'createdAt', name: 'Creation Date' },
        { id: 'closeDate', name: 'Close Date' },
        { id: 'registeredUsers', name: 'Registered Users' },
        { id: 'appliedCredits', name: 'Applied Credits' },
        { id: 'signedCredits', name: 'Signed Credits' },
      ]}
    />,
    <CreditCropsFilter key="crops" source="crops" label="Unique Crops" />,
    <DealerChannelFilter key="source" source="source" label="Channel" />,
    <DealerOwnerFilter key="owner" source="owner" label="Owner" />,
    <CreditStatesFilter key="state" source="state" label="States" nameOnly />,
    <SelectArrayInput
      key="status"
      source="status"
      choices={dealerStatuses}
      className="MS-field"
      style={{ width: 80 }}
    />,
    <SelectArrayInput
      key="priority"
      source="priority"
      label="Type"
      choices={dealerTypes}
      className="MS-field"
      style={{ width: 80 }}
    />,
    <SelectArrayInput
      key="ibm"
      source="ibm"
      label="IBM"
      choices={months.map(t => ({ id: t, name: t }))}
      className="MS-field"
      style={{ width: 80 }}
    />,
    <SelectInput
      key="documents"
      source="documents"
      label="Documents"
      choices={[{ id: 'complete', name: 'Complete' }, { id: 'empty', name: 'Empty' }]}
      className={'MS-field'}
    />,
    <SelectInput
      key="hasAccount"
      source="hasAccount"
      label="With active account"
      choices={[{ id: 'yes', name: 'Yes' }, { id: 'no', name: 'No' }]}
      className={'MS-field'}
    />,
  ];

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <>
      <GridList {...props}
        filters={filters}
        sort={{ field: 'id', order: 'DESC' }}
        customCreate={openCreator}
        className={cls.list}
        exporter={exporter}
        editComponent={rbacEdit ? <DealerListActions /> : undefined}
        mergeButton
        summary={<Summary />}
      >
        <Grid
          rowClick="edit"
          className={cls.grid}
          defaultColumns={'code account name phone priority segment status countUsers'.split(' ')}
        >
          <TextField source="code" name="code" label="Code" />
          <FunctionField
            source="manager"
            label="Account"
            sortable={false}
            render={(dealer: Dealer) => {
              return dealer.manager?.id ? (
                <CheckCircleFilled style={{ color: '#1DC265', fontSize: 18 }} />
              ) : null;
            }}
          />
          <TextField source="activationTitle" label="Activation" cellClassName={cls.nameCell} />
          <TextField source="name" name="name" cellClassName={cls.nameCell} label="Name" />
          <TextField source="cropsIbmMonthList" name="IBM" label="IBM" />
          <FunctionField
            source="cropIbms"
            label="Unique Crops"
            sortable={false}
            render={(dealer: Dealer) => {
              if (!dealer.cropsIbm || !Array.isArray(dealer.cropsIbm)) {
                return '-';
              }

              const cropNames = dealer.cropsIbm
                .map(cropIbm => cropIbm.crop)
                .filter(Boolean);

              return cropNames.length > 0 ? cropNames.join(', ') : '-';
            }}
          />
          <TextField source="status.name" label="Status" />
          <TextField source="priorityObj.name" label="Type" />
          <TextField source="addedByUserFullName" label="Added by User" />
          <TextField source="owner.name" label="Owner" />
          <CountUsersBtn source="countUsers" label="Go to Users" />
          <CountCreditsBtn
            source="countApplyingCredits"
            label="Go to Applying Credits"
            counterField="countApplyingCredits"
            statuses={[
              CreditStatus.COMPLETED_FILE, CreditStatus.APPROVED,
              CreditStatus.MODERATION, CreditStatus.BANK_REVISION,
            ]}
          />
          <CountCreditsBtn
            source="countSignedCredits"
            label="Go to Signed Credits"
            counterField="countSignedCredits"
            statuses={[
              CreditStatus.SIGNED, CreditStatus.DELAY,
              CreditStatus.REPAID, CreditStatus.RESTRUCTURE,
            ]}
          />
          <CountCreditsBtn
            source="countActiveCredits"
            label="Go to Active Credits"
            counterField="countActiveCredits"
            statuses={[CreditStatus.SIGNED, CreditStatus.DELAY, CreditStatus.RESTRUCTURE]}
          />
          <TextField source="phone" label="Phone" />
          <TextField source="email" label="E-mail" />
          <TextField source="states" label="States" />
          <TextField source="source" label="Channel" />
          <DateField source="createdAt" label="Created At" showTime />
          <DateField source="closeDate" label="Close Date" />
          <ButtonLinkField source="sfAccountLink"
            text="SalesForce Account" label="SalesForce Account" center isBlue />
          <FunctionField
            source="documents"
            label="Documents"
            sortable={false}
            render={(dealer: Dealer) => (
              <div>{dealer.documents?.length ? 'Complete' : 'Empty'}</div>
            )}
          />
        </Grid>
      </GridList>

      {isBuyersCreatorActive ? (
        <BuyerCreator
          type={modalType.current}
          close={closeCreator}
          values={selectedBuyer}
        />
      ) : null}
    </>
  );
};
